/**
 * Hauptkomponente der Portfolio Wizard Anwendung
 * Verwendet das Cloudscape Design System für eine konsistente und moderne Benutzeroberfläche
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  AppLayout,
  ContentLayout,
  Header,
  SpaceBetween,
  Tabs,
  Alert
} from '@cloudscape-design/components';
import '@cloudscape-design/global-styles/index.css';
import { applyMode, Mode } from '@cloudscape-design/global-styles';
// Removed Amplify Authenticator - using API Key authentication instead

import { AppZustand, SteuerParameter, TransaktionsDaten, PortfolioPosition, EntnahmeSimulation } from '@/types';
import { NAVIGATION_TABS } from '@/utils/constants';
import { PortfolioService } from '@/services/portfolioService';
import { getDetailedVersionInfo } from '@/utils/version';
import { trackPageView, getStoredConsent, loadGtag, revokeAnalyticsStorage } from '@/utils/analytics';
import AnalyticsConsentBanner from '@/components/AnalyticsConsentBanner';
import BuyMeCoffeeButton from '@/components/BuyMeCoffeeButton';
import Schritt0 from '@/pages/Schritt0';
import Schritt1 from '@/pages/Schritt1';
import Schritt2 from '@/pages/Schritt2';
import Schritt3 from '@/pages/Schritt3';
import Schritt4 from '@/pages/Schritt4';
import ParqetCallback from '@/pages/ParqetCallback';

const STEUER_PARAMETER_STORAGE_KEY = 'portfolio-wizard-steuer-parameter';

function loadSteuerParameterFromStorage(): SteuerParameter | null {
  try {
    const stored = localStorage.getItem(STEUER_PARAMETER_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as SteuerParameter;
    if (parsed?.steuerjahr && parsed?.familienstand && parsed?.kirchensteuer) {
      return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

function saveSteuerParameterToStorage(parameter: SteuerParameter | null): void {
  try {
    if (parameter) {
      localStorage.setItem(STEUER_PARAMETER_STORAGE_KEY, JSON.stringify(parameter));
    } else {
      localStorage.removeItem(STEUER_PARAMETER_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * Hauptkomponente mit State Management und Navigation
 */
const App: React.FC = () => {
  // Frame-busting protection: Prevent unauthorized iframe embedding
  useEffect(() => {
    // Check if the application is running inside an iframe
    if (window.self !== window.top) {
      try {
        // Check if the parent frame is from an authorized domain
        const parentOrigin = document.referrer;
        const allowedDomains = ['https://www.svenesis.org'];
        
        // If parent is from an allowed domain, allow embedding
        const isAuthorized = allowedDomains.some(domain => 
          parentOrigin.startsWith(domain)
        );
        
        if (!isAuthorized) {
          // Attempt to break out of unauthorized iframe
          if (window.top) {
            window.top.location.href = window.self.location.href;
          }
        } else {
          // Authorized embedding detected
        }
      } catch {
        // If cross-origin restrictions prevent referrer check, try alternative method
        try {
          // Use postMessage to communicate with parent and verify origin
          // Restrict targetOrigin so only the allowed parent receives the message
          window.parent.postMessage(
            { type: 'PORTFOLIO_WIZARD_ORIGIN_CHECK' },
            'https://www.svenesis.org'
          );
          
          // Listen for response from parent
          const handleOriginResponse = (event: MessageEvent) => {
            const allowedOrigins = ['https://www.svenesis.org'];
            
            if (allowedOrigins.includes(event.origin)) {
              window.removeEventListener('message', handleOriginResponse);
              return;
            }
            
            // Unauthorized embedding detected - show warning
            showUnauthorizedEmbeddingWarning();
            window.removeEventListener('message', handleOriginResponse);
          };
          
          window.addEventListener('message', handleOriginResponse);
          
          // Fallback: If no response within 1 second, assume unauthorized
          setTimeout(() => {
            window.removeEventListener('message', handleOriginResponse);
            showUnauthorizedEmbeddingWarning();
          }, 1000);
          
        } catch {
          // Final fallback - show warning for any iframe embedding we can't verify
          showUnauthorizedEmbeddingWarning();
        }
      }
    }
    
    function showUnauthorizedEmbeddingWarning() {
      // Create and show warning overlay
      const warningOverlay = document.createElement('div');
      warningOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 0, 0, 0.9);
        color: white;
        z-index: 999999;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-family: Arial, sans-serif;
        font-size: 18px;
        line-height: 1.5;
      `;
      
      warningOverlay.innerHTML = `
        <div style="max-width: 600px; padding: 20px;">
          <h2 style="color: #fff; margin-bottom: 20px;">🚫 Unauthorized Embedding Detected</h2>
          <p>This application is not authorized to run in an iframe for security reasons.</p>
          <p>Please visit the application directly at its official URL.</p>
          <button onclick="window.open('${window.location.origin}', '_blank')" 
                  style="margin-top: 20px; padding: 10px 20px; font-size: 16px; 
                         background: #fff; color: #000; border: none; border-radius: 4px; 
                         cursor: pointer;">
            Open Application Directly
          </button>
        </div>
      `;
      
      document.body.appendChild(warningOverlay);
    }
  }, []);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AppContent />
    </Router>
  );
};

/**
 * App-Inhalt mit Routing und State Management
 * Getrennt von der Hauptkomponente für bessere Router-Integration
 */
const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [gaReady, setGaReady] = useState(false);
  const [cookieSettingsOpenRequest, setCookieSettingsOpenRequest] = useState(0);

  const openCookieSettings = () => {
    setCookieSettingsOpenRequest((n) => n + 1);
  };

  useEffect(() => {
    if (getStoredConsent() !== 'granted') return;
    let cancelled = false;
    loadGtag()
      .then(() => {
        if (!cancelled) setGaReady(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!gaReady) return;
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search, gaReady]);

  // Zentraler Anwendungszustand (steuerParameter aus localStorage für OAuth-Redirect)
  const [appZustand, setAppZustand] = useState<AppZustand>(() => ({
    steuerParameter: loadSteuerParameterFromStorage(),
    transaktionsDaten: [],
    portfolioPositionen: [],
    entnahmeSimulation: null,
    istImportAbgeschlossen: false,
    hatVerkaufsEingaben: false,
    aktuellerSchritt: 1,
    // Initialize persistent Schritt 3 form values
    zielEntnahmebetrag: '',
    portfolioPercentageRaw: 0.0,
    sparerPauschbetragEnabled: false,
    // Initialize persistent Schritt 4 form values
    jahreseinkommen: '50000',
    sonderausgaben: '0'
  }));

  // Navigation State
  const [activeTabId, setActiveTabId] = useState<string>('schritt0');

  // Theme Management
  const [currentTheme, setCurrentTheme] = useState<Mode>(() => {
    // Detect system theme preference
    if (typeof window !== 'undefined') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? Mode.Dark : Mode.Light;
      const savedTheme = localStorage.getItem('cloudscape-theme') as Mode;
      return savedTheme || systemTheme;
    }
    return Mode.Light;
  });

  // Apply theme on mount and theme change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      applyMode(currentTheme);
      localStorage.setItem('cloudscape-theme', currentTheme);
    }
  }, [currentTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleThemeChange = (e: MediaQueryListEvent) => {
        const newTheme = e.matches ? Mode.Dark : Mode.Light;
        setCurrentTheme(newTheme);
      };
      
      mediaQuery.addListener(handleThemeChange);
      return () => mediaQuery.removeListener(handleThemeChange);
    }
  }, []);

  // Sync active tab and step from route (deferred to avoid synchronous setState in effect)
  useEffect(() => {
    const currentPath = location.pathname;
    const pathToTab: Record<string, { tab: string; step: number }> = {
      '/schritt0': { tab: 'schritt0', step: 0 },
      '/schritt1': { tab: 'schritt1', step: 1 },
      '/schritt2': { tab: 'schritt2', step: 2 },
      '/schritt3': { tab: 'schritt3', step: 3 },
      '/schritt4': { tab: 'schritt4', step: 4 }
    };
    const match = pathToTab[currentPath];
    if (match) {
      queueMicrotask(() => {
        setActiveTabId(match.tab);
        setAppZustand(prev => ({ ...prev, aktuellerSchritt: match.step }));
      });
    }
  }, [location.pathname]);

  /**
   * Handler für Berechnungsparameter-Updates aus Schritt 1
   */
  const handleSteuerParameterUpdate = (parameter: SteuerParameter) => {
    saveSteuerParameterToStorage(parameter);
    setAppZustand(prev => ({
      ...prev,
      steuerParameter: parameter
    }));
  };

  /**
   * Handler für Import aus Schritt 2
   */
  const handleImport = (
    daten: TransaktionsDaten[],
    currentPricesByAsset?: Record<string, number>,
    logosByAsset?: Record<string, string>,
    soldAssetIdentifiers?: string[]
  ) => {
    // Portfolio-Positionen aus Transaktionsdaten berechnen
    const portfolioPositionen = PortfolioService.calculatePortfolioPositions(
      daten,
      currentPricesByAsset,
      soldAssetIdentifiers,
      logosByAsset
    );

    setAppZustand(prev => ({
      ...prev,
      transaktionsDaten: daten,
      portfolioPositionen,
      logosByAsset,
      istImportAbgeschlossen: true
    }));
  };

  /**
   * Handler für das Zurücksetzen des Imports
   */
  const handleImportReset = () => {
    setAppZustand(prev => ({
      ...prev,
      transaktionsDaten: [],
      portfolioPositionen: [],
      logosByAsset: undefined,
      istImportAbgeschlossen: false,
      hatVerkaufsEingaben: false,
      entnahmeSimulation: null,
      // Reset Schritt 3 form values on import reset
      zielEntnahmebetrag: '',
      portfolioPercentageRaw: 0.0,
      sparerPauschbetragEnabled: false,
      // Reset Schritt 4 form values on import reset
      jahreseinkommen: '50000',
      sonderausgaben: '0'
    }));
  };

  /**
   * Handler für Portfolio-Position-Updates aus Schritt 3
   */
  const handlePortfolioUpdate = (positionen: PortfolioPosition[]) => {
    const hatVerkauf = positionen.some(p => !p.exclude && p.sellPercentage > 0);
    
    setAppZustand(prev => ({
      ...prev,
      portfolioPositionen: positionen,
      hatVerkaufsEingaben: hatVerkauf
    }));
  };

  /**
   * Handler für Entnahme-Simulation-Updates aus Schritt 3
   */
  const handleSimulationUpdate = (simulation: EntnahmeSimulation) => {
    setAppZustand(prev => ({
      ...prev,
      entnahmeSimulation: simulation
    }));
  };

  /**
   * Handler für Ziel-Entnahmebetrag-Updates aus Schritt 3
   */
  const handleZielEntnahmebetragUpdate = (betrag: string) => {
    setAppZustand(prev => ({
      ...prev,
      zielEntnahmebetrag: betrag
    }));
  };

  /**
   * Handler für Portfolio-Percentage-Updates aus Schritt 3
   */
  const handlePortfolioPercentageUpdate = (percentage: number) => {
    setAppZustand(prev => ({
      ...prev,
      portfolioPercentageRaw: percentage
    }));
  };

  /**
   * Handler für Jahreseinkommen-Updates aus Schritt 4
   */
  const handleJahreseinkommenUpdate = (jahreseinkommen: string) => {
    setAppZustand(prev => ({
      ...prev,
      jahreseinkommen
    }));
  };

  /**
   * Handler für Sonderausgaben-Updates aus Schritt 4
   */
  const handleSonderausgabenUpdate = (sonderausgaben: string) => {
    setAppZustand(prev => ({
      ...prev,
      sonderausgaben
    }));
  };

  /**
   * Handler für Sparer-Pauschbetrag-Updates aus Schritt 3
   */
  const handleSparerPauschbetragUpdate = (enabled: boolean) => {
    setAppZustand(prev => ({
      ...prev,
      sparerPauschbetragEnabled: enabled
    }));
  };

  /**
   * Tab-Change Handler
   */
  const handleTabChange = (detail: { activeTabId: string }) => {
    const tabId = detail.activeTabId;
    setActiveTabId(tabId);
    
    // Navigation basierend auf Tab-ID
    if (tabId === 'schritt0') {
      navigate('/schritt0');
    } else if (tabId === 'schritt1') {
      navigate('/schritt1');
    } else if (tabId === 'schritt2') {
      navigate('/schritt2');
    } else if (tabId === 'schritt3') {
      navigate('/schritt3');
    } else if (tabId === 'schritt4') {
      navigate('/schritt4');
    }
  };

  /**
   * Bestimmt, ob ein Tab aktiviert werden soll
   */
  const istTabAktivierbar = (tabId: string): boolean => {
    switch (tabId) {
      case 'schritt0':
        return true; // Schritt 0 ist immer verfügbar (Info und FAQ)
      case 'schritt1':
        return true; // Schritt 1 ist immer verfügbar
      case 'schritt2':
        return appZustand.steuerParameter !== null; // Schritt 2 nur nach Berechnungsparameter-Eingabe
      case 'schritt3':
        return appZustand.istImportAbgeschlossen && appZustand.portfolioPositionen.length > 0; // Schritt 3: Beispielhafte Simulation von Verkaufs-Szenarien nur nach Import mit Portfolio-Daten
      case 'schritt4':
        return appZustand.hatVerkaufsEingaben; // Schritt 4 nur wenn beispielhafte Verkaufsreihenfolge vorhanden ist
      default:
        return false;
    }
  };

  // Tab-Konfiguration mit dynamischer Aktivierung
  const tabs = NAVIGATION_TABS.map(tab => ({
    id: tab.id,
    label: tab.label,
    disabled: !istTabAktivierbar(tab.id)
  }));

  return (
    <>
    <AppLayout
      headerSelector="#top-navigation"
      navigationHide={true}
      toolsHide={true}
      disableContentPaddings={true}
      content={
        <div className="app-page-gutter">
        <div className="portfolio-wizard-bmc-slot">
          <BuyMeCoffeeButton />
        </div>
        <ContentLayout
          disableOverlap={true}
          header={
            <div className="portfolio-wizard-header-slot-wrap">
            <Header
              variant="h1"
              description={
                <>
                  {getDetailedVersionInfo()}
                  {' • '}
                  <a href="https://www.svenesis.org/contact/contact_de.html" target="_blank" rel="noopener noreferrer">Impressum</a>
                  {' • '}
                  <a href="https://www.svenesis.org/privacy/privacy_de.html" target="_blank" rel="noopener noreferrer">Datenschutz</a>
                  {' • '}
                  <a
                    id="cookie-einstellungen"
                    href="#cookie-einstellungen"
                    onClick={(e) => {
                      e.preventDefault();
                      openCookieSettings();
                    }}
                  >
                    Cookie-Einstellungen
                  </a>
                </>
              }
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                <img
                  src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDMyIDMyIj4KICA8ZGVmcz4KICAgIDxsaW5lYXJHcmFkaWVudCBpZD0iZ3JhZGllbnQiIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPgogICAgICA8c3RvcCBvZmZzZXQ9IjAlIiBzdHlsZT0ic3RvcC1jb2xvcjojMDA3M2JiO3N0b3Atb3BhY2l0eToxIiAvPgogICAgICA8c3RvcCBvZmZzZXQ9IjEwMCUiIHN0eWxlPSJzdG9wLWNvbG9yOiNmZjk5MDA7c3RvcC1vcGFjaXR5OjEiIC8+CiAgICA8L2xpbmVhckdyYWRpZW50PgogIDwvZGVmcz4KICA8Y2lyY2xlIGN4PSIxNiIgY3k9IjE2IiByPSIxNCIgZmlsbD0idXJsKCNncmFkaWVudCkiIHN0cm9rZT0iIzIzMmYzZSIgc3Ryb2tlLXdpZHRoPSIyIi8+CiAgPHRleHQgeD0iMTYiIHk9IjIwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSJ3aGl0ZSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmb250LXdlaWdodD0iYm9sZCI+UzwvdGV4dD4KPC9zdmc+"
                  alt="Portfolio Wizard Logo"
                  style={{ width: 24, height: 24 }}
                />
                <span>Portfolio Wizard für Parqet</span>
              </span>
            </Header>
            </div>
          }
        >
          <SpaceBetween direction="vertical" size="l">
            {/* Hinweise über den aktuellen Status */}
            {!appZustand.steuerParameter && (
              <Alert 
                type="info"
                dismissAriaLabel="Schließen"
                header="Berechnungsparameter erforderlich"
              >
                Bitte füllen Sie zunächst die Berechnungsparameter in Schritt 1 aus, um fortzufahren.
              </Alert>
            )}
            
            {appZustand.steuerParameter && !appZustand.istImportAbgeschlossen && appZustand.aktuellerSchritt > 1 && (
              <Alert 
                type="info"
                dismissAriaLabel="Schließen"
                header="Import erforderlich"
              >
                Bitte importieren Sie Transaktionsdaten in Schritt 2, um Portfolio-Analysen durchzuführen.
              </Alert>
            )}

            {appZustand.istImportAbgeschlossen && appZustand.portfolioPositionen.length === 0 && appZustand.aktuellerSchritt > 2 && (
              <Alert 
                type="warning"
                dismissAriaLabel="Schließen"
                header="Keine Portfolio-Positionen gefunden"
              >
                Keine Portfolio-Positionen gefunden. Prüfen Sie Ihre Transaktionsdaten in Schritt 2.
              </Alert>
            )}
            
            {/* Hauptnavigation als Tabs */}
            <Tabs
              activeTabId={activeTabId}
              onChange={({ detail }) => handleTabChange(detail)}
              tabs={tabs}
              ariaLabel="Hauptnavigation"
              variant="default"
            />

            {/* Hauptinhalt basierend auf der Route */}
            <main role="main" aria-label="Hauptinhalt">
              <Routes>
                <Route 
                  path="/schritt0" 
                  element={<Schritt0 />} 
                />
                <Route 
                  path="/schritt1" 
                  element={
                    <Schritt1 
                      steuerParameter={appZustand.steuerParameter}
                      onParameterUpdate={handleSteuerParameterUpdate}
                    />
                  } 
                />
                <Route 
                  path="/schritt2" 
                  element={
                    appZustand.steuerParameter ? (
                      <Schritt2
                        transaktionsDaten={appZustand.transaktionsDaten}
                        logosByAsset={appZustand.logosByAsset}
                        istImportAbgeschlossen={appZustand.istImportAbgeschlossen}
                        onImport={handleImport}
                        onImportReset={handleImportReset}
                      />
                    ) : (
                      <Alert 
                        type="warning"
                        dismissAriaLabel="Schließen"
                        header="Voraussetzung nicht erfüllt"
                      >
                        Bitte füllen Sie zunächst die Berechnungsparameter in Schritt 1 aus.
                      </Alert>
                    )
                  } 
                />
                <Route 
                  path="/schritt3" 
                  element={
                    appZustand.istImportAbgeschlossen && appZustand.portfolioPositionen.length > 0 ? (
                      <Schritt3 
                        portfolioPositionen={appZustand.portfolioPositionen}
                        entnahmeSimulation={appZustand.entnahmeSimulation}
                        hatVerkaufsEingaben={appZustand.hatVerkaufsEingaben}
                        steuerParameter={appZustand.steuerParameter}
                        onPortfolioUpdate={handlePortfolioUpdate}
                        onSimulationUpdate={handleSimulationUpdate}
                        zielEntnahmebetrag={appZustand.zielEntnahmebetrag}
                        onZielEntnahmebetragUpdate={handleZielEntnahmebetragUpdate}
                        portfolioPercentageRaw={appZustand.portfolioPercentageRaw}
                        onPortfolioPercentageUpdate={handlePortfolioPercentageUpdate}
                        sparerPauschbetragEnabled={appZustand.sparerPauschbetragEnabled}
                        onSparerPauschbetragUpdate={handleSparerPauschbetragUpdate}
                      />
                    ) : (
                      <Alert 
                        type="warning"
                        dismissAriaLabel="Schließen"
                        header="Voraussetzung nicht erfüllt"
                      >
                        Bitte importieren Sie zunächst Transaktionsdaten in Schritt 2, um Portfolio-Analysen durchzuführen.
                      </Alert>
                    )
                  } 
                />
                <Route 
                  path="/schritt4" 
                  element={
                    appZustand.hatVerkaufsEingaben ? (
                      <Schritt4 
                        portfolioPositionen={appZustand.portfolioPositionen}
                        entnahmeSimulation={appZustand.entnahmeSimulation}
                        hatVerkaufsEingaben={appZustand.hatVerkaufsEingaben}
                        steuerParameter={appZustand.steuerParameter}
                        jahreseinkommen={appZustand.jahreseinkommen}
                        sonderausgaben={appZustand.sonderausgaben}
                        onJahreseinkommenUpdate={handleJahreseinkommenUpdate}
                        onSonderausgabenUpdate={handleSonderausgabenUpdate}
                        sparerPauschbetragEnabled={appZustand.sparerPauschbetragEnabled}
                      />
                    ) : (
                      <Alert 
                        type="warning"
                        dismissAriaLabel="Schließen"
                        header="Voraussetzung nicht erfüllt"
                      >
                        Bitte führen Sie zunächst eine beispielhafte Verkaufsreihenfolge in Schritt 3: Beispielhafte Simulation von Verkaufs-Szenarien durch, um den Modellvergleich zu verwenden.
                      </Alert>
                    )
                  } 
                />
                <Route path="/callback" element={<ParqetCallback />} />
                <Route path="/" element={<Navigate to="/schritt0" replace />} />
                <Route path="*" element={<Navigate to="/schritt0" replace />} />
              </Routes>
            </main>
          </SpaceBetween>
        </ContentLayout>
        </div>
      }
    />
    <AnalyticsConsentBanner
      openRequest={cookieSettingsOpenRequest}
      onGranted={() => setGaReady(true)}
      onDenied={() => {
        revokeAnalyticsStorage();
        setGaReady(false);
      }}
    />
    </>
  );
};

export default App;